// «الصيانة» — super admin: link each maintenance location to an ops branch (GET/PUT
// /maintenance/mapping). A suggestion counts in the figures until confirmed («مطابقة مقترحة»);
// a confirmed row is never changed by a sync. The card stays mounted across syncs: a new sync
// (`reloadKey`) refreshes the rows and keeps the unsaved edits of locations that still exist.
import { useEffect, useRef, useState } from "react";
import { Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MAINT } from "@/constants/testIds";
import { fmtTemplate } from "./maintText";

const NONE = "__none__";
const STATE_TONE = { confirmed: "secondary", suggested: "warning", unmapped: "outline" };

export function MappingCard({ t, lang, dir, onSaved, reloadKey = 0 }) {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});        // location_code -> branch id | NONE | "reset"
    const [saving, setSaving] = useState(false);
    const seq = useRef(0);

    useEffect(() => {
        const mine = ++seq.current;          // a slower, older answer never replaces a newer one
        api.get("/maintenance/mapping")
            .then((r) => {
                if (mine !== seq.current) return;
                setData(r.data);
                // keep what was being edited; drop edits of locations the feed no longer lists
                const codes = new Set((r.data?.rows || []).map((x) => x.location_code));
                setDraft((d) => Object.fromEntries(Object.entries(d).filter(([code]) => codes.has(code))));
            })
            .catch((e) => { if (mine === seq.current) toast.error(apiErrorText(e)); });
    }, [reloadKey]);

    const name = (o) => (lang === "ar" ? o?.name_ar || o?.name_en : o?.name_en || o?.name_ar) || "";
    const rows = data?.rows || [];
    // only active, non-finance-only branches can be chosen (PUT /mapping refuses the rest)
    const branches = data?.branches || [];
    const optionsFor = (r) => (r.branch_id && !branches.some((b) => b.id === r.branch_id)
        // a row confirmed to a branch that is no longer offered still shows its own branch
        ? [...branches, { id: r.branch_id, name_ar: r.branch_name_ar, name_en: r.branch_name_en, legacy: true }]
        : branches);
    const changes = Object.keys(draft).length;
    const suggested = rows.filter((r) => r.state === "suggested");

    const save = async (items) => {
        if (!items.length) return;
        setSaving(true);
        try {
            const r = await api.put("/maintenance/mapping", { items });
            seq.current += 1;                // an older load still in flight must not undo this
            setData((d) => ({ ...d, rows: r.data.rows }));
            setDraft({});
            toast.success(t("mt_map_saved"));
            onSaved && onSaved();
        } catch (e) {
            toast.error(apiErrorText(e));
        } finally {
            setSaving(false);
        }
    };
    const saveDraft = () => save(Object.entries(draft).map(([code, v]) => (v === "reset"
        ? { location_code: code, confirmed: false }
        : { location_code: code, branch_id: v === NONE ? null : v, confirmed: true })));
    const confirmAll = () => save(suggested.map((r) => ({ location_code: r.location_code, branch_id: r.branch_id, confirmed: true })));

    if (!data) return null;
    return (
        <Card data-testid={MAINT.mapping} className="space-y-3 rounded-2xl border-[color:var(--border-soft)] p-4 shadow-[var(--shadow-sm)]">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-1.5 text-sm font-extrabold text-[color:var(--ink)]">
                        <Link2 className="h-4 w-4 text-[color:var(--brand-brown)]" /><span>{t("mt_map_title")}</span>
                    </h3>
                    <p className="mt-0.5 text-xs text-[color:var(--ink-muted)]">{t("mt_map_hint")}</p>
                </div>
                {suggested.length ? (
                    <Button data-testid={MAINT.mappingConfirmAll} variant="outline" size="sm" onClick={confirmAll} disabled={saving}>
                        <span>{t("mt_map_confirm_all")}</span>
                    </Button>
                ) : null}
            </div>
            {!rows.length ? (
                <p data-testid={MAINT.mappingEmpty} className="rounded-xl surface-2 px-3 py-4 text-center text-xs text-[color:var(--ink-muted)]">
                    {t("mt_map_empty")}
                </p>
            ) : null}
            <ul className="divide-y divide-[color:var(--separator)]">
                {rows.map((r) => {
                    const d = draft[r.location_code];
                    const value = d && d !== "reset" ? d : (r.branch_id || NONE);
                    return (
                        <li key={r.location_code} data-testid={MAINT.mappingRow(r.location_code)} data-state={r.state}
                            className="grid grid-cols-1 gap-2 py-2.5 sm:grid-cols-[1fr_16rem_auto] sm:items-center">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-[color:var(--ink)]">
                                    <bdi>{name(r) || r.location_code}</bdi> <span dir="ltr" className="text-[11px] text-[color:var(--ink-faint)]">{r.location_code}</span>
                                </p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                    <Badge variant={STATE_TONE[r.state] || "outline"} className="px-2 py-0 text-[10px]">
                                        <span>{t(`mt_map_state_${r.state}`)}</span>
                                    </Badge>
                                    {r.type ? <span className="text-[10px] text-[color:var(--ink-faint)]"><bdi>{r.type}</bdi></span> : null}
                                </div>
                            </div>
                            <Select value={value} dir={dir} disabled={saving}
                                    onValueChange={(v) => setDraft((x) => ({ ...x, [r.location_code]: v }))}>
                                <SelectTrigger data-testid={MAINT.mappingSelect(r.location_code)} className="h-10 w-full text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NONE}>{t("mt_map_no_branch")}</SelectItem>
                                    {optionsFor(r).map((b) => (
                                        <SelectItem key={b.id} value={b.id} disabled={!!b.legacy}>{name(b) || b.id}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {r.confirmed ? (
                                <button type="button" disabled={saving} onClick={() => setDraft((x) => ({ ...x, [r.location_code]: "reset" }))}
                                        className="justify-self-start text-[11px] font-semibold text-[color:var(--ink-muted)] underline-offset-2 hover:underline">
                                    <span>{t("mt_map_reset")}</span>
                                </button>
                            ) : r.state === "suggested" && !d ? (
                                <button type="button" disabled={saving} onClick={() => setDraft((x) => ({ ...x, [r.location_code]: r.branch_id }))}
                                        className="justify-self-start text-[11px] font-bold text-[color:var(--brand-brown)] underline-offset-2 hover:underline">
                                    <span>{t("mt_map_confirm")}</span>
                                </button>
                            ) : <span className="hidden sm:block" />}
                        </li>
                    );
                })}
            </ul>
            <div className="flex flex-wrap items-center justify-end gap-2">
                {changes ? <span className="text-xs text-[color:var(--ink-muted)]">{fmtTemplate(t("mt_map_changes"), { n: changes })}</span> : null}
                <Button data-testid={MAINT.mappingSave} onClick={saveDraft} disabled={!changes || saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    <span>{t("mt_map_save")}</span>
                </Button>
            </div>
        </Card>
    );
}
